import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { ShoppingCart, Store, AlertTriangle, ArrowLeft, Shield, Sparkles, CheckCircle2, CreditCard, Mail, Lock } from "lucide-react";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRateLimitCheck } from "@/hooks/useRateLimitCheck";
import { isValidRedirectUrl } from "@/hooks/useSecuritySession";
import { useIsMobile } from "@/hooks/use-mobile";
import { InlineAlert } from "@/components/InlineAlert";
import { useInlineAlert } from "@/hooks/useInlineAlert";
import { translateAuthError } from "@/lib/authErrorTranslations";
import { lovable } from "@/integrations/lovable";
import { ErrorSupportLinks } from "@/components/ErrorSupportLinks";
import {
  ensureCanonicalHost,
  isSocialWebView,
  saveOAuthDestination,
} from "@/lib/oauthFlow";
import logo from "@/assets/logo.png";
import SEO from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import heroImage from "@/assets/hero-costa-rica.jpg";

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.61 20.08H42V20H24v8h11.3c-1.65 4.66-6.08 8-11.3 8-6.63 0-12-5.37-12-12s5.37-12 12-12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.05 29.27 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.34-.14-2.65-.39-3.92z"/>
    <path fill="#FF3D00" d="M6.31 14.69l6.57 4.82C14.66 15.11 18.96 12 24 12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.05 29.27 4 24 4 16.32 4 9.66 8.34 6.31 14.69z"/>
    <path fill="#4CAF50" d="M24 44c5.17 0 9.86-1.98 13.41-5.2l-6.19-5.24C29.21 35.09 26.71 36 24 36c-5.2 0-9.62-3.32-11.28-7.95l-6.52 5.02C9.5 39.56 16.23 44 24 44z"/>
    <path fill="#1976D2" d="M43.61 20.08H42V20H24v8h11.3c-.79 2.24-2.24 4.16-4.09 5.56l6.19 5.24C36.97 39.21 44 34 44 24c0-1.34-.14-2.65-.39-3.92z"/>
  </svg>
);

const AppleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M17.05 12.04c-.03-2.93 2.39-4.34 2.5-4.41-1.36-1.99-3.48-2.26-4.24-2.29-1.8-.18-3.52 1.06-4.44 1.06-.92 0-2.34-1.04-3.85-1.01-1.98.03-3.81 1.15-4.83 2.92-2.06 3.57-.53 8.85 1.48 11.75.98 1.42 2.15 3.01 3.68 2.95 1.48-.06 2.04-.96 3.83-.96 1.79 0 2.29.96 3.86.93 1.59-.03 2.6-1.44 3.57-2.87 1.13-1.64 1.59-3.24 1.62-3.32-.04-.02-3.11-1.2-3.14-4.75zM14.13 3.62c.81-.99 1.36-2.36 1.21-3.72-1.17.05-2.59.78-3.43 1.76-.75.87-1.41 2.27-1.23 3.6 1.31.1 2.64-.66 3.45-1.64z"/>
  </svg>
);


const passwordSchema = z.string()
  .min(6, { message: "La contraseña debe tener al menos 6 caracteres" });

const loginSchema = z.object({
  email: z.string().email({ message: "Email inválido" }).max(255, { message: "Email muy largo" }),
  password: z.string().min(1, { message: "Ingresa tu contraseña" })
});

const signupSchema = z.object({
  fullName: z.string().min(2, { message: "El nombre debe tener al menos 2 caracteres" }).max(100, { message: "Nombre muy largo" }),
  email: z.string().email({ message: "Email inválido" }).max(255, { message: "Email muy largo" }),
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Las contraseñas no coinciden",
  path: ["confirmPassword"]
});

type AuthMode = "login" | "signup" | "seller-signup";

const AUTH_SEO: Record<AuthMode, { title: string; description: string; path: string; index: boolean }> = {
  "seller-signup": {
    title: "Crear tienda en línea en Costa Rica | Vendé en TicoMarket",
    description: "Abrí tu tienda en línea en la Feria en Línea de Costa Rica. Vendé artesanías, productos hechos a mano y tu marca a compradores de todo el país con pago por tarjeta y SINPE Móvil.",
    path: "/registro-vendedor",
    index: true,
  },
  signup: {
    title: "Crear cuenta | TicoMarket – Feria en Línea de Costa Rica",
    description: "Creá tu cuenta gratis en TicoMarket y comprá productos únicos de emprendedores y artesanos costarricenses. Guardá favoritos, seguí tus pedidos y pagá con SINPE Móvil o tarjeta.",
    path: "/registro",
    index: true,
  },
  login: {
    title: "Iniciar sesión | TicoMarket",
    description: "Entrá a tu cuenta de TicoMarket para comprar, seguir tus pedidos o administrar tu tienda.",
    path: "/iniciar-sesion",
    index: false,
  },
};

const Auth = ({ mode }: { mode?: AuthMode } = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockCountdown, setBlockCountdown] = useState(0);
  const [attemptCount, setAttemptCount] = useState(0);
  const [registrationType, setRegistrationType] = useState<'buyer' | 'seller' | null>(null);
  const { user, signIn, signUp } = useAuth();
  const { alert, showSuccess, showError, clearAlert } = useInlineAlert();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkRateLimit, recordLoginAttempt } = useRateLimitCheck();
  const isMobile = useIsMobile();
  
  const rawRedirect = searchParams.get("redirect") || (mode === "seller-signup" ? "/seller-onboarding" : "/");
  const redirectTo = isValidRedirectUrl(rawRedirect) ? rawRedirect : "/";
  const intent = searchParams.get("intent") || (mode === "seller-signup" ? "sell" : null);
  const tabParam = searchParams.get("tab") || (mode === "signup" || mode === "seller-signup" ? "signup" : null);
  const seoMode: AuthMode = mode ?? (tabParam === "signup" ? (intent === "sell" ? "seller-signup" : "signup") : "login");
  const seo = AUTH_SEO[seoMode];
  const oauthError = searchParams.get("authError");
  const inWebView = typeof window !== "undefined" && isSocialWebView();

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" }
  });

  const signupForm = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: { fullName: "", email: "", password: "", confirmPassword: "" }
  });

  useEffect(() => {
    if (blockCountdown > 0) {
      const timer = setTimeout(() => {
        setBlockCountdown(blockCountdown - 1);
        if (blockCountdown <= 1) {
          setIsBlocked(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [blockCountdown]);

  useEffect(() => {
    const checkRoleAndRedirect = async () => {
      if (user) {
        // OAuth consent flow: always return to the consent screen.
        if (redirectTo.startsWith("/.lovable/oauth/consent")) {
          window.location.href = redirectTo;
          return;
        }

        // Check if user is a seller

        const { data: sellerProfile } = await supabase
          .from('seller_profiles')
          .select('user_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (sellerProfile) {
          navigate('/shop-manager');
          return;
        }

        // No seller profile yet — check if they started seller onboarding
        // (e.g. created a Stripe checkout or submitted a SINPE payment).
        // If so, send them back to the onboarding to finish.
        const { data: sub } = await supabase
          .from('seller_subscriptions' as any)
          .select('seller_id')
          .eq('seller_id', user.id)
          .maybeSingle();

        if (sub) {
          navigate('/seller-onboarding');
          return;
        }

        navigate(redirectTo);
      }
    };

    checkRoleAndRedirect();
  }, [user, navigate, redirectTo]);

  const checkLoginBlocked = useCallback(async (email: string) => {
    const result = await checkRateLimit(email);
    if (result.blocked) {
      setIsBlocked(true);
      setBlockCountdown(result.remaining_seconds);
      setAttemptCount(result.attempt_count);
      return true;
    }
    setAttemptCount(result.attempt_count);
    return false;
  }, [checkRateLimit]);

  const onLogin = async (values: z.infer<typeof loginSchema>) => {
    const blocked = await checkLoginBlocked(values.email);
    if (blocked) {
      showError(`Cuenta bloqueada. Espera ${formatTime(blockCountdown)} para intentar de nuevo.`);
      return;
    }

    setIsLoading(true);
    const { error } = await signIn(values.email, values.password);
    
    if (error) {
      const result = await recordLoginAttempt(values.email, false);
      if (result.blocked) {
        setIsBlocked(true);
        setBlockCountdown(result.remaining_seconds);
      }
      setAttemptCount(result.attempt_count);
      
      showError(translateAuthError(error.message));
    } else {
      await recordLoginAttempt(values.email, true);
      showSuccess("Has iniciado sesión correctamente");
      // Redirect is handled by useEffect when user state updates
    }
    setIsLoading(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')} min`;
    }
    return `${secs}s`;
  };

  const onSignup = async (values: z.infer<typeof signupSchema>) => {
    setIsLoading(true);
    
    // For sellers: create account immediately and redirect to onboarding
    if (registrationType === 'seller') {
      try {
        // Create a timeout promise to prevent infinite loading
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('La solicitud tardó demasiado. Revisa tu conexión e intenta de nuevo.')), 20000);
        });

        const signupPromise = supabase.auth.signUp({
          email: values.email,
          password: values.password,
          options: {
            emailRedirectTo: `${window.location.origin}/seller-onboarding`,
            data: { full_name: values.fullName }
          }
        });

        const { data, error } = await Promise.race([signupPromise, timeoutPromise]) as Awaited<typeof signupPromise>;
        
        // Log for debugging (no sensitive data)
        console.log('[Seller Signup]', { 
          hasError: !!error, 
          errorMessage: error?.message,
          hasSession: !!data?.session,
          hasUser: !!data?.user 
        });

        if (error) {
          showError(translateAuthError(error.message) || "Error al crear la cuenta. Intenta de nuevo.");
          return;
        }

        // If no session was returned (rare with auto-confirm on), sign in immediately
        if (!data?.session) {
          if (data?.user) {
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email: values.email,
              password: values.password,
            });
            if (signInError) {
              showError(translateAuthError(signInError.message) || "No se pudo iniciar sesión. Intenta de nuevo.");
              return;
            }
          } else {
            showError("No se pudo crear la cuenta. Intenta de nuevo.");
            return;
          }
        }

        // Success - user is authenticated with active session
        showSuccess("¡Cuenta creada! Redirigiendo al onboarding...");
        navigate('/seller-onboarding');
        
      } catch (err: any) {
        console.error('[Seller Signup Error]', err);
        showError(translateAuthError(err?.message) || "Error inesperado. Intenta de nuevo.");
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // For buyers: create account immediately
    try {
      const { error } = await signUp(values.email, values.password, values.fullName);
      
      if (error) {
        showError(translateAuthError(error.message));
      } else {
        showSuccess("Tu cuenta ha sido creada correctamente.");
        navigate(redirectTo || '/');
      }
    } catch (err: any) {
      console.error('[Buyer Signup Error]', err);
      showError(translateAuthError(err?.message) || "Error inesperado. Intenta de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  const onResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      showError("Ingresa tu email");
      return;
    }
    
    setIsLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    
    if (error) {
      showError(translateAuthError(error.message));
    } else {
      showSuccess("Revisa tu correo para restablecer tu contraseña");
      setShowResetPassword(false);
      setResetEmail("");
    }
    setIsLoading(false);
  };

  if (showResetPassword) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Mobile back button */}
        {isMobile && (
          <header className="p-4 pt-safe">
            <Button variant="ghost" size="icon" onClick={() => setShowResetPassword(false)} aria-label="Volver">
              <ArrowLeft className="h-5 w-5" />
            </Button>

          </header>
        )}
        
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className={`w-full ${isMobile ? 'border-0 shadow-none bg-transparent' : 'max-w-md'}`}>
            <CardHeader className={isMobile ? 'px-0' : ''}>
              <CardTitle className="text-2xl text-center">Recuperar Contraseña</CardTitle>
              <CardDescription className="text-center">
                Te enviaremos un enlace para restablecer tu contraseña
              </CardDescription>
            </CardHeader>
            <CardContent className={isMobile ? 'px-0' : ''}>
              {/* Inline Alert */}
              <InlineAlert alert={alert} onDismiss={clearAlert} className="mb-4" />
              
              <form onSubmit={onResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input 
                    type="email" 
                    placeholder="tu@email.com" 
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="h-12"
                  />
                </div>
                <Button type="submit" className="w-full h-12" disabled={isLoading}>
                  {isLoading ? "Enviando..." : "Enviar Enlace"}
                </Button>
                {!isMobile && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => setShowResetPassword(false)}
                  >
                    Volver al inicio de sesión
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const getIntentMessage = () => {
    if (intent === "buy") {
      return (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2">
          <ShoppingCart className="h-4 w-4" />
          <span>Inicia sesión para completar tu compra</span>
        </div>
      );
    }
    if (intent === "sell") {
      return (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2">
          <Store className="h-4 w-4" />
          <span>Inicia sesión para comenzar a vender</span>
        </div>
      );
    }
    return null;
  };

  const handleOAuth = async (provider: "google" | "apple") => {
    // Ida y vuelta deben ocurrir en el mismo dominio (www), o el "state" no valida.
    if (ensureCanonicalHost()) return;

    setIsLoading(true);
    // If signing up as seller, ensure we land on the onboarding flow
    const targetRedirect =
      registrationType === "seller" || intent === "sell"
        ? "/seller-onboarding"
        : redirectTo;
    saveOAuthDestination(targetRedirect);
    const result = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: `${window.location.origin}/auth/callback`,
    });
    if (result.error) {
      setIsLoading(false);
      showError(
        translateAuthError(result.error.message) ||
          `Error al iniciar sesión con ${provider === "google" ? "Google" : "Apple"}`
      );
    }
  };

  const OAuthButtons = ({ context }: { context: "login" | "signup" }) => (
    <div className="space-y-2.5">
      {oauthError && (
        <Alert variant="destructive" className="text-left">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <span className="block font-medium">{oauthError}</span>
            <span className="block mt-1 text-xs opacity-90">
              Volvé a intentarlo desde este mismo navegador, sin cambiar de app ni de
              pestaña.
            </span>
            <ErrorSupportLinks errorText={oauthError} errorType="Inicio de sesión" />
          </AlertDescription>
        </Alert>
      )}
      {inWebView && (
        <Alert className="text-left">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Estás dentro del navegador de una red social. Para entrar con Google o
            Apple, abrí ticomarket.com en Safari o Chrome.
          </AlertDescription>
        </Alert>
      )}
      <Button
        type="button"
        variant="outline"
        className="w-full h-12 gap-3 font-medium border-border/80 hover:bg-muted/60 hover:border-border transition-all"
        disabled={isLoading}
        onClick={() => handleOAuth("google")}
      >
        <GoogleIcon />
        {context === "login" ? "Continuar con Google" : "Registrarse con Google"}
      </Button>
      <Button
        type="button"
        variant="outline"
        className="w-full h-12 gap-3 font-medium bg-foreground text-background hover:bg-foreground/90 hover:text-background border-foreground transition-all"
        disabled={isLoading}
        onClick={() => handleOAuth("apple")}
      >
        <AppleIcon />
        {context === "login" ? "Continuar con Apple" : "Registrarse con Apple"}
      </Button>
    </div>
  );

  const Divider = ({ label = "o continúa con email" }: { label?: string }) => (
    <div className="relative my-5">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t border-border/70" />
      </div>
      <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
        <span className="bg-card px-3 text-muted-foreground font-medium">{label}</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <SEO title={seo.title} description={seo.description} url={`https://www.ticomarket.com${seo.path}`} />
      <Helmet>
        {(!mode || !seo.index) && <meta name="robots" content="noindex, follow" />}
      </Helmet>
      <div className="relative min-h-screen grid lg:grid-cols-2">
        {/* Editorial brand panel — desktop only */}
        <aside className="hidden lg:flex flex-col justify-between p-12 xl:p-16 bg-muted/40 border-r border-border relative">
          <Link to="/" className="relative flex items-center gap-2 w-fit">
            <img src={logo} alt="TicoMarket" className="h-12 w-auto" />
          </Link>

          <div className="relative space-y-8 max-w-md">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary">
              Hecho a mano · Costa Rica
            </span>
            <div className="aspect-[5/4] overflow-hidden rounded-2xl border border-border">
              <img src={heroImage} alt="Productos artesanales de Costa Rica" className="w-full h-full object-cover" />
            </div>
            <div className="space-y-3">
              <h1 className="font-display text-4xl xl:text-5xl leading-[1.05] text-foreground">
                La feria en línea<br />hecha en Costa Rica.
              </h1>
              <p className="text-base text-muted-foreground leading-relaxed">
                Conectamos compradores con tiendas y piezas únicas de emprendedores ticos.
              </p>
            </div>

            <ul className="divide-y divide-border border-y border-border">
              {[
                { icon: Sparkles, text: "Miles de productos de tiendas locales" },
                { icon: Shield, text: "Pagos protegidos y datos cifrados" },
                { icon: CheckCircle2, text: "Soporte humano de lunes a sábado" },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-3 py-3 text-foreground">
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-[15px]">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="relative text-sm text-muted-foreground">
            © {new Date().getFullYear()} TicoMarket · Feria en Línea
          </p>
        </aside>

        {/* Form side */}
        <main className="relative flex flex-col">
          {/* Mobile top bar */}
          <div className="lg:hidden flex items-center justify-between px-4 pt-3 pb-1">
            <Link to="/" className="flex items-center">
              <img src={logo} alt="TicoMarket" className="h-8 w-auto" />
            </Link>
            <Link to="/" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1">
              Cancelar
            </Link>
          </div>

          <div className="flex-1 flex items-start lg:items-center justify-center px-3 pt-2 pb-24 sm:p-6 lg:p-8">
            <h1 className="sr-only">Acceso a TicoMarket</h1>
            <div className="w-full max-w-md">

              <Card className="border border-border bg-card rounded-2xl shadow-sm">
                <CardHeader className="pb-4 pt-6 lg:pt-8 px-5 lg:px-7 space-y-2 text-center">
                  <span className="text-[11px] font-semibold tracking-widest uppercase text-primary">
                    Feria en Línea
                  </span>
                  <CardTitle className="font-display text-3xl lg:text-4xl leading-[1.1] text-foreground">
                    {tabParam === "signup" ? "Crea tu cuenta" : "Bienvenido de vuelta"}
                  </CardTitle>
                  <CardDescription className="text-sm lg:text-[15px] text-muted-foreground">
                    {tabParam === "signup"
                      ? "Empieza a comprar o vender en minutos"
                      : "Inicia sesión para continuar"}
                  </CardDescription>
                  {getIntentMessage()}
                </CardHeader>

                <CardContent className="pb-7 px-5 lg:px-7">
                  <InlineAlert alert={alert} onDismiss={clearAlert} className="mb-4" />

                  <Tabs defaultValue={tabParam === "signup" ? "signup" : "login"} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 h-11 lg:h-12 p-1 bg-muted rounded-full">
                      <TabsTrigger value="login" className="text-sm font-semibold rounded-full data-[state=active]:bg-background data-[state=active]:shadow-sm">
                        Iniciar Sesión
                      </TabsTrigger>
                      <TabsTrigger value="signup" className="text-sm font-semibold rounded-full data-[state=active]:bg-background data-[state=active]:shadow-sm">
                        Registrarse
                      </TabsTrigger>
                    </TabsList>


                    {/* LOGIN */}
                    <TabsContent value="login" className="mt-5">
                      <OAuthButtons context="login" />
                      <Divider />

                      {isBlocked && (
                        <Alert variant="destructive" className="mb-4">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>
                            Cuenta bloqueada. Espera {formatTime(blockCountdown)}.
                          </AlertDescription>
                        </Alert>
                      )}
                      {!isBlocked && attemptCount >= 2 && (
                        <Alert className="mb-4 border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/40">
                          <AlertTriangle className="h-4 w-4 text-yellow-600" />
                          <AlertDescription className="text-yellow-700 dark:text-yellow-400 text-sm">
                            {attemptCount} intentos fallidos. Después de 5 intentos tu cuenta será bloqueada.
                          </AlertDescription>
                        </Alert>
                      )}

                      <Form {...loginForm}>
                        <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
                          <FormField
                            control={loginForm.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium">Email</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <Input
                                      type="email"
                                      placeholder="tu@email.com"
                                      {...field}
                                      disabled={isBlocked}
                                      className="h-12 pl-10"
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={loginForm.control}
                            name="password"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-center justify-between">
                                  <FormLabel className="text-sm font-medium">Contraseña</FormLabel>
                                  <button
                                    type="button"
                                    onClick={() => setShowResetPassword(true)}
                                    className="text-xs font-medium text-primary hover:underline"
                                  >
                                    ¿La olvidaste?
                                  </button>
                                </div>
                                <FormControl>
                                  <PasswordInput
                                    placeholder="••••••••"
                                    {...field}
                                    disabled={isBlocked}
                                    className="h-12"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <Button
                            type="submit"
                            className="w-full h-12 text-base font-semibold shadow-md shadow-primary/20"
                            disabled={isLoading || isBlocked}
                          >
                            {isBlocked
                              ? `Bloqueado (${formatTime(blockCountdown)})`
                              : isLoading
                              ? "Iniciando..."
                              : "Iniciar Sesión"}
                          </Button>
                        </form>
                      </Form>
                    </TabsContent>

                    {/* SIGNUP */}
                    <TabsContent value="signup" className="mt-5">
                      {!registrationType ? (
                        <div className="space-y-3">
                          <p className="text-sm font-semibold text-foreground text-center mb-1">
                            ¿Cómo deseas usar TicoMarket?
                          </p>
                          <p className="text-xs text-muted-foreground text-center mb-4">
                            Elegí una opción para continuar
                          </p>

                          <button
                            type="button"
                            onClick={() => setRegistrationType('buyer')}
                            className="group relative w-full p-4 lg:p-5 rounded-2xl border border-border bg-gradient-to-br from-background to-primary/[0.03] hover:border-primary/60 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-0.5 transition-all duration-200 text-left flex items-center gap-3.5 overflow-hidden"
                          >
                            <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/5 blur-2xl group-hover:bg-primary/15 transition-colors" />
                            <div className="relative h-11 w-11 lg:h-12 lg:w-12 rounded-xl lg:rounded-2xl bg-gradient-to-br from-primary/15 to-primary/25 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 group-hover:scale-105 transition-transform">
                              <ShoppingCart className="h-5 w-5" strokeWidth={2.2} />
                            </div>
                            <div className="relative min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-bold text-foreground text-[15px] lg:text-base">
                                  Quiero Comprar
                                </h3>
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground uppercase tracking-wider">
                                  Gratis
                                </span>
                              </div>
                              <p className="text-[13px] lg:text-sm text-muted-foreground mt-0.5 leading-snug">
                                Miles de productos de tiendas ticas
                              </p>
                            </div>
                            <ArrowLeft className="relative h-4 w-4 text-muted-foreground rotate-180 opacity-60 lg:opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" />
                          </button>

                          <button
                            type="button"
                            onClick={() => setRegistrationType('seller')}
                            className="group relative w-full p-4 lg:p-5 rounded-2xl border border-border bg-gradient-to-br from-background to-tropical/[0.04] hover:border-tropical/60 hover:shadow-lg hover:shadow-tropical/10 hover:-translate-y-0.5 transition-all duration-200 text-left flex items-center gap-3.5 overflow-hidden"
                          >
                            <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-tropical/5 blur-2xl group-hover:bg-tropical/15 transition-colors" />
                            <div className="relative h-11 w-11 lg:h-12 lg:w-12 rounded-xl lg:rounded-2xl bg-gradient-to-br from-tropical/15 to-tropical/25 text-tropical flex items-center justify-center shrink-0 ring-1 ring-tropical/20 group-hover:scale-105 transition-transform">
                              <Store className="h-5 w-5" strokeWidth={2.2} />
                            </div>
                            <div className="relative min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-bold text-foreground text-[15px] lg:text-base">Quiero Vender</h3>
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-tropical/15 text-tropical uppercase tracking-wider">
                                  Pro
                                </span>
                              </div>
                              <p className="text-[13px] lg:text-sm text-muted-foreground mt-0.5 leading-snug">
                                Abre tu tienda y vende a todo el país
                              </p>
                            </div>
                            <ArrowLeft className="relative h-4 w-4 text-muted-foreground rotate-180 opacity-60 lg:opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" />
                          </button>

                          {/* Trust signals */}
                          <div className="flex items-center justify-center gap-5 pt-4 mt-2 border-t border-border/50">
                            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-medium">
                              <Shield className="h-3.5 w-3.5 text-tropical" />
                              Datos cifrados
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-medium">
                              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                              Pagos seguros
                            </div>
                          </div>
                        </div>
                      ) : (

                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setRegistrationType(null)}
                            className="mb-3 -ml-2 h-8"
                          >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Cambiar tipo de cuenta
                          </Button>

                          <div
                            className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border ${
                              registrationType === 'buyer'
                                ? 'bg-primary/5 border-primary/20 text-primary'
                                : 'bg-tropical/5 border-tropical/20 text-tropical'
                            }`}
                          >
                            {registrationType === 'buyer' ? (
                              <ShoppingCart className="h-4 w-4" />
                            ) : (
                              <Store className="h-4 w-4" />
                            )}
                            <span className="text-sm font-semibold">
                              {registrationType === 'buyer' ? 'Cuenta de comprador' : 'Cuenta de vendedor'}
                            </span>
                          </div>

                          {registrationType === 'seller' && (
                            <div className="mb-4 p-3 rounded-lg bg-gradient-to-br from-tropical/5 to-primary/5 border border-tropical/15 space-y-2">
                              <div className="flex items-start gap-2">
                                <CreditCard className="h-4 w-4 text-tropical mt-0.5 shrink-0" />
                                <div className="text-xs text-foreground/80 leading-relaxed">
                                  <p className="font-semibold text-foreground mb-0.5">Siguiente paso: activar membresía</p>
                                  <p>Después de crear tu cuenta te llevaremos al onboarding para configurar tu tienda y activar tu membresía mensual.</p>
                                </div>
                              </div>
                            </div>
                          )}

                          <OAuthButtons context="signup" />
                          <Divider label="o usa tu email" />

                          <Form {...signupForm}>
                            <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
                              <FormField
                                control={signupForm.control}
                                name="fullName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-sm font-medium">Nombre Completo</FormLabel>
                                    <FormControl>
                                      <Input placeholder="Juan Pérez" {...field} className="h-12" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={signupForm.control}
                                name="email"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-sm font-medium">Email</FormLabel>
                                    <FormControl>
                                      <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                        <Input type="email" placeholder="tu@email.com" {...field} className="h-12 pl-10" />
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={signupForm.control}
                                name="password"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-sm font-medium">Contraseña</FormLabel>
                                    <FormControl>
                                      <PasswordInput placeholder="Mínimo 6 caracteres" {...field} className="h-12" />
                                    </FormControl>
                                    <PasswordStrengthIndicator password={field.value} />
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={signupForm.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-sm font-medium">Confirmar Contraseña</FormLabel>
                                    <FormControl>
                                      <PasswordInput placeholder="Repite tu contraseña" {...field} className="h-12" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <Button
                                type="submit"
                                className={`w-full h-12 text-base font-semibold shadow-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-100 disabled:bg-muted disabled:text-foreground/70 disabled:shadow-none ${
                                  registrationType === 'seller'
                                    ? 'shadow-primary/20'
                                    : 'shadow-primary/20'
                                }`}
                                disabled={isLoading}
                              >
                                {isLoading
                                  ? "Creando cuenta..."
                                  : registrationType === 'seller'
                                  ? "Crear cuenta y continuar al pago"
                                  : "Crear Cuenta"}
                              </Button>

                              <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                                Al registrarte aceptas nuestros{" "}
                                <Link to="/terminos-y-condiciones" className="text-primary hover:underline">Términos</Link>
                                {" "}y{" "}
                                <Link to="/politica-de-privacidad" className="text-primary hover:underline">Política de Privacidad</Link>.
                              </p>
                            </form>
                          </Form>
                        </>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {/* Trust signals — mobile/below card */}
              <div className="mt-5 flex items-center justify-center gap-5 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" />
                  <span>Datos cifrados</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Shield className="h-3.5 w-3.5" />
                  <span>Pagos seguros</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Auth;
